import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import FileButton from 'components/FileButton'
import AdvisorSelect from 'client-user-management/components/AdvisorSelect'
import {
  Form,
  FormColumn,
  FormColumns,
  FormGroupContainer,
  FormRow,
  TextAreaContainer,
  TextInputContainer,
} from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from 'components/ModalSheet'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'
import { generateCsvTemplate } from 'utils/csv-parser'
import { match } from 'utils/pattern-match-util'
import { pluralize } from 'utils/pluralization-util'
import { ClientInvitationViewModel } from './ClientInvitationViewModel'
import styles from './ClientInvitationDialog.sass'

interface CustomerInvitationFormProps {
  vm: ClientInvitationViewModel
}

const SingleCustomerForm = observer(({ vm }: CustomerInvitationFormProps) => {
  return (
    <>
      <FormRow>
        <FormGroupContainer
          label="Email address"
          error={vm.customerValidation.getError('customerEmail')}
        >
          <TextInputContainer
            model={vm}
            field="customerEmail"
            type="email"
            placeholder="customer@example.com"
            disabled={vm.submit.pending}
            data-test="client-invitation-customer-email"
          />
        </FormGroupContainer>
      </FormRow>

      <FormRow>
        <FormColumns>
          <FormColumn>
            <FormGroupContainer
              label="First name"
              error={vm.customerValidation.getError('customerGivenName')}
            >
              <TextInputContainer
                model={vm}
                field="customerGivenName"
                placeholder="John"
                disabled={vm.submit.pending}
                data-test="client-invitation-customer-given-name"
              />
            </FormGroupContainer>
          </FormColumn>
          <FormColumn>
            <FormGroupContainer
              label="Last name"
              error={vm.customerValidation.getError('customerFamilyName')}
            >
              <TextInputContainer
                model={vm}
                field="customerFamilyName"
                placeholder="Doe"
                disabled={vm.submit.pending}
                data-test="client-invitation-customer-family-name"
              />
            </FormGroupContainer>
          </FormColumn>
        </FormColumns>
      </FormRow>

      <FormRow>
        <FormGroupContainer
          label="Phone number"
          sublabel="(optional)"
          error={vm.customerValidation.getError('customerPhoneNumber')}
        >
          <TextInputContainer
            model={vm}
            field="customerPhoneNumber"
            type="tel"
            placeholder="+1 555-123-4567"
            disabled={vm.submit.pending}
            data-test="client-invitation-customer-phone"
          />
        </FormGroupContainer>
      </FormRow>
    </>
  )
})

const BulkUploadForm = observer(({ vm }: CustomerInvitationFormProps) => {
  const handleFileChange = React.useCallback(
    (files: FileList) => {
      if (files.length > 0) {
        vm.handleCsvUpload(files[0])
      }
    },
    [vm],
  )

  const handleDownloadTemplate = React.useCallback(() => {
    const content = generateCsvTemplate()
    const blob = new Blob([content], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'customer_import_template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }, [])

  return (
    <>
      <FormRow>
        <FormGroupContainer label="Upload CSV file">
          <div className={styles.uploadRow}>
            <FileButton
              accept=".csv"
              onChange={handleFileChange}
              disabled={vm.submit.pending}
            >
              <Button
                type="secondary"
                disabled={vm.submit.pending}
                dataTest="client-invitation-bulk-upload-button"
              >
                Choose file
              </Button>
            </FileButton>
            <span className={styles.fileName}>
              {vm.bulkUploadFileName || 'No file selected'}
            </span>
          </div>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className={styles.downloadLink}
          >
            Download sample template
          </button>
        </FormGroupContainer>
      </FormRow>

      {vm.bulkUploadError && (
        <FormRow>
          <div className={styles.errorMessage}>{vm.bulkUploadError}</div>
        </FormRow>
      )}

      {vm.bulkCustomers.length > 0 && (
        <FormRow>
          <FormGroupContainer
            label={`Preview (${vm.validBulkCustomers.length} valid of ${vm.bulkCustomers.length})`}
          >
            <div className={styles.previewTable}>
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {vm.bulkCustomers.map((customer, index) => (
                    <tr
                      key={customer.rowNumber}
                      className={customer.isValid ? '' : styles.invalidRow}
                    >
                      <td>{customer.email || '-'}</td>
                      <td>{customer.given_name || '-'}</td>
                      <td>{customer.family_name || '-'}</td>
                      <td>{customer.phone_number || '-'}</td>
                      <td>
                        {customer.isValid ? (
                          <span className={styles.validStatus}>Valid</span>
                        ) : (
                          <span
                            className={styles.invalidStatus}
                            title={customer.errors.join(', ')}
                          >
                            {customer.errors[0]}
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => vm.removeBulkCustomer(index)}
                          className={styles.removeButton}
                          disabled={vm.submit.pending}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FormGroupContainer>
        </FormRow>
      )}
    </>
  )
})

const BulkResultsView = observer(({ vm }: CustomerInvitationFormProps) => {
  const successCount = vm.successfulBulkResults.length
  const failedCount = vm.failedBulkResults.length

  return (
    <div className={styles.resultsView}>
      <div className={styles.resultsSummary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryCount}>{successCount}</span>
          <span className={styles.summaryLabel}>Created</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryCountFailed}>{failedCount}</span>
          <span className={styles.summaryLabel}>Failed</span>
        </div>
      </div>

      {successCount > 0 && (
        <div className={styles.resultsSection}>
          <h4 className={styles.resultsSectionTitle}>
            Successfully Created ({successCount})
          </h4>
          <div className={styles.resultsTable}>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                {vm.successfulBulkResults.map((result, index) => (
                  <tr key={index} className={styles.successRow}>
                    <td>{result.email}</td>
                    <td>
                      {result.givenName} {result.familyName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {failedCount > 0 && (
        <div className={styles.resultsSection}>
          <h4 className={styles.resultsSectionTitle}>Failed ({failedCount})</h4>
          <div className={styles.resultsTable}>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {vm.failedBulkResults.map((result, index) => (
                  <tr key={index} className={styles.failedRow}>
                    <td>{result.email}</td>
                    <td>
                      {result.givenName} {result.familyName}
                    </td>
                    <td className={styles.errorCell}>{result.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
})

const CustomerInvitationForm = observer(
  ({ vm }: CustomerInvitationFormProps) => {
    return (
      <>
        <FormRow>
          <RadioButtonGroup label="Entry Mode" size="big-wide">
            <RadioButtonItem
              active={!vm.bulkUploadMode}
              onClick={() => vm.setBulkUploadMode(false)}
              disabled={vm.submit.pending}
            >
              Single
            </RadioButtonItem>
            <RadioButtonItem
              active={vm.bulkUploadMode}
              onClick={() => vm.setBulkUploadMode(true)}
              disabled={vm.submit.pending}
            >
              Bulk Upload
            </RadioButtonItem>
          </RadioButtonGroup>
        </FormRow>

        {vm.bulkUploadMode ? (
          <BulkUploadForm vm={vm} />
        ) : (
          <SingleCustomerForm vm={vm} />
        )}
      </>
    )
  },
)

interface RegularInvitationFormProps {
  vm: ClientInvitationViewModel
}

const RegularInvitationForm = observer(({ vm }: RegularInvitationFormProps) => {
  return (
    <>
      <FormRow>
        <FormGroupContainer
          label="Email address"
          error={vm.invitationValidation.getError('emailAddressTextArea')}
        >
          <TextAreaContainer
            model={vm}
            field="emailAddressTextArea"
            rows={3}
            placeholder="e.g. timmy.taxes@taxfyle.com, cindy.cpa@firm.com"
            help="Enter one or more email addresses, separated by commas."
            data-test="client-invitation-email-textarea"
          />
        </FormGroupContainer>
      </FormRow>
    </>
  )
})

interface ClientInvitationDialogProps {
  vm: ClientInvitationViewModel
}

export const ClientInvitationDialog = observer(
  ({ vm }: ClientInvitationDialogProps) => {
    const renderForm = () =>
      match(vm.signInType, {
        AccessLink: () => <CustomerInvitationForm vm={vm} />,
        UsernamePassword: () => <RegularInvitationForm vm={vm} />,
      })

    // Show results view after bulk submission
    if (vm.showBulkResults) {
      return (
        <ModalSheet
          showing={vm.showing}
          onRequestClose={() => vm.dismissBulkResults(false)}
          disableCloseOnOutsideClick
        >
          <ModalSheetHeader withCloseButton />

          <ModalSheetContent>
            <ModalSheetSection horizontallySpaced>
              <ModalSheetContentTitle>
                Bulk Import Results
              </ModalSheetContentTitle>
              <ModalSheetContentDescription compact>
                {vm.successfulBulkResults.length > 0 &&
                vm.failedBulkResults.length === 0
                  ? 'All customers were created successfully.'
                  : vm.failedBulkResults.length > 0 &&
                      vm.successfulBulkResults.length === 0
                    ? 'All customer creations failed.'
                    : 'Some customers were created, but others failed.'}
              </ModalSheetContentDescription>
            </ModalSheetSection>

            <ModalSheetSection spaced>
              <BulkResultsView vm={vm} />
            </ModalSheetSection>
          </ModalSheetContent>

          <ModalSheetFooter>
            <ModalSheetFooterSpacer />
            <ModalSheetFooterButtonGroup>
              {vm.failedBulkResults.length > 0 && (
                <Button
                  type="secondary"
                  onClick={() => vm.dismissBulkResults(true)}
                  dataTest="client-invitation-retry-failed"
                >
                  Retry Failed ({vm.failedBulkResults.length})
                </Button>
              )}
              <Button
                type="primary"
                onClick={() => vm.dismissBulkResults(false)}
                dataTest="client-invitation-done"
              >
                Done
              </Button>
            </ModalSheetFooterButtonGroup>
          </ModalSheetFooter>
        </ModalSheet>
      )
    }

    return (
      <ModalSheet
        showing={vm.showing}
        onRequestClose={vm.close}
        disableCloseOnOutsideClick
      >
        <ModalSheetHeader withCloseButton />

        <ModalSheetContent>
          <ModalSheetSection horizontallySpaced>
            <ModalSheetContentTitle>Invite client</ModalSheetContentTitle>
            <ModalSheetContentDescription compact>
              {vm.signInType === 'AccessLink'
                ? `Fill in the customer information to allow access link-based sign in.${vm.assignsToSelf ? " They'll be assigned to you as their advisor." : ''}`
                : "Fill in the emails of the users you'd like to invite to the Customer Portal."}
            </ModalSheetContentDescription>
          </ModalSheetSection>

          <ModalSheetSection spaced>
            <Form id="clientInvitation" onSubmit={vm.submit}>
              {vm.canUseAccessLink && vm.canUseUsernameAndPassword && (
                <FormRow>
                  <RadioButtonGroup label="Sign-in Type" size="big-wide">
                    <RadioButtonItem
                      active={vm.signInType === 'UsernamePassword'}
                      onClick={() => vm.setSignInType('UsernamePassword')}
                      disabled={vm.submit.pending}
                    >
                      Username & Password
                    </RadioButtonItem>
                    <RadioButtonItem
                      active={vm.signInType === 'AccessLink'}
                      onClick={() => vm.setSignInType('AccessLink')}
                      disabled={vm.submit.pending}
                    >
                      Access Link
                    </RadioButtonItem>
                  </RadioButtonGroup>
                </FormRow>
              )}

              {renderForm()}

              {vm.canChooseAdvisor && vm.signInType === 'AccessLink' && (
                <AdvisorSelect
                  vm={vm.advisorSelect}
                  disabled={vm.submit.pending}
                />
              )}
            </Form>
          </ModalSheetSection>
        </ModalSheetContent>

        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button type="default" onClick={vm.close}>
              Close
            </Button>
            <Button
              submit
              form="clientInvitation"
              type="primary"
              disabled={
                vm.submit.pending ||
                (vm.signInType === 'AccessLink' &&
                  vm.bulkUploadMode &&
                  vm.validBulkCustomers.length === 0)
              }
              dataTest="client-invitation-submit"
            >
              {vm.signInType === 'AccessLink'
                ? vm.bulkUploadMode
                  ? `Create ${vm.validBulkCustomers.length} ${pluralize('customer', vm.validBulkCustomers.length)}`
                  : 'Create new customer'
                : `Send ${pluralize('invite', vm.emailAddresses.length)}`}
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>
    )
  },
)
